import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getDiasFestivos, getEmpleados, getVacaciones, type Vacacion } from "@/lib/api";
import { diasHabilesClip, MESES, pad2 } from "@/lib/tipos";

const HOY = new Date();

function aniosOpciones(): number[] {
  const y = HOY.getFullYear();
  return [y - 2, y - 1, y, y + 1];
}

function nombreCorto(email: string, nombre: string | null): string {
  return nombre && nombre.trim() ? nombre : email.split("@")[0];
}

// Días hábiles (L–V, sin festivos) de una vacación que caen en el mes mesIdx (0-11).
function diasHabilesEnMes(
  v: Vacacion,
  anio: number,
  mesIdx: number,
  festivos: Set<string>,
): number {
  const ultimo = new Date(anio, mesIdx + 1, 0).getDate();
  const mStart = `${anio}-${pad2(mesIdx + 1)}-01`;
  const mEnd = `${anio}-${pad2(mesIdx + 1)}-${pad2(ultimo)}`;
  return diasHabilesClip(v.fecha_inicio, v.fecha_fin, mStart, mEnd, festivos);
}

type SortKey = "nombre" | "usados" | "bolsa" | "restan";

export function EquipoAnioPage() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [sortKey, setSortKey] = useState<SortKey>("nombre");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const navigate = useNavigate();

  const handleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      // Numéricas arrancan de mayor a menor; el nombre, alfabético.
      setSortDir(k === "nombre" ? "asc" : "desc");
    }
  };

  const empleadosQ = useQuery({ queryKey: ["empleados"], queryFn: () => getEmpleados() });
  const vacQ = useQuery({
    queryKey: ["vacaciones", "anio", anio],
    queryFn: () => getVacaciones({ anio }),
  });
  const festivosQ = useQuery({
    queryKey: ["dias-festivos"],
    queryFn: getDiasFestivos,
    staleTime: Infinity,
  });
  const festivos = useMemo(
    () => new Set((festivosQ.data ?? []).map((f) => f.fecha)),
    [festivosQ.data],
  );

  // empleado -> [12] días hábiles de VACACIONES por mes (solo tipo "vacaciones",
  // que es lo que descuenta la bolsa).
  const matriz = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const v of vacQ.data ?? []) {
      if (v.tipo !== "vacaciones") continue;
      let arr = map.get(v.empleado_email);
      if (!arr) {
        arr = new Array(12).fill(0);
        map.set(v.empleado_email, arr);
      }
      for (let m = 0; m < 12; m++) arr[m] += diasHabilesEnMes(v, anio, m, festivos);
    }
    return map;
  }, [vacQ.data, anio, festivos]);

  // email -> bolsa anual (default 20)
  const bolsaPorEmpleado = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of empleadosQ.data ?? []) map.set(e.email, e.dias_bolsa);
    return map;
  }, [empleadosQ.data]);

  const filas = useMemo(() => {
    const base = (empleadosQ.data ?? []).map((e) => ({
      email: e.email,
      nombre: nombreCorto(e.email, e.nombre),
      fecha_ingreso: e.fecha_ingreso,
    }));
    const list =
      base.length > 0
        ? base
        : [...new Set((vacQ.data ?? []).map((v) => v.empleado_email))].map((email) => ({
            email,
            nombre: nombreCorto(
              email,
              (vacQ.data ?? []).find((v) => v.empleado_email === email)?.empleado_nombre ?? null,
            ),
            fecha_ingreso: null as string | null,
          }));
    const conTotales = list
      .map((emp) => {
        const meses = matriz.get(emp.email) ?? new Array(12).fill(0);
        const usados = meses.reduce((a: number, b: number) => a + b, 0);
        // Si ingresó DESPUÉS del año consultado, ese año no generaba
        // vacaciones: no aparece (o, si tuviera días capturados, bolsa 0).
        const ingresoAnio = emp.fecha_ingreso ? Number(emp.fecha_ingreso.slice(0, 4)) : null;
        const laboraba = ingresoAnio == null || ingresoAnio <= anio;
        const bolsa = laboraba ? (bolsaPorEmpleado.get(emp.email) ?? 20) : 0;
        return { ...emp, meses, usados, bolsa, restan: bolsa - usados, laboraba };
      })
      .filter((e) => e.laboraba || e.usados > 0);
    conTotales.sort((a, b) => {
      const cmp =
        sortKey === "nombre"
          ? a.nombre.localeCompare(b.nombre)
          : a[sortKey] - b[sortKey];
      // Desempate estable por nombre.
      const c = cmp !== 0 ? cmp : a.nombre.localeCompare(b.nombre);
      return sortDir === "asc" ? c : -c;
    });
    return conTotales;
  }, [empleadosQ.data, vacQ.data, matriz, bolsaPorEmpleado, anio, sortKey, sortDir]);

  const maxMes = useMemo(() => {
    let mx = 0;
    for (const arr of matriz.values()) for (const n of arr) if (n > mx) mx = n;
    return mx;
  }, [matriz]);

  const bg = (n: number): string | undefined => {
    if (n <= 0 || maxMes <= 0) return undefined;
    const alpha = 0.15 + 0.55 * (n / maxMes);
    return `rgba(37, 99, 235, ${alpha.toFixed(3)})`; // azul-600
  };

  // Línea vertical de "hoy" (solo en el año en curso): en la columna del mes
  // actual, a la altura proporcional del día dentro del mes.
  const hoyMes = anio === HOY.getFullYear() ? HOY.getMonth() : null;
  const diasMesActual = new Date(HOY.getFullYear(), HOY.getMonth() + 1, 0).getDate();
  const hoyX = `${(((HOY.getDate() - 0.5) / diasMesActual) * 100).toFixed(1)}%`;
  const hoyTitulo = `Hoy · ${pad2(HOY.getDate())}/${pad2(HOY.getMonth() + 1)}/${HOY.getFullYear()}`;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Equipo · por año</h1>
          <p className="text-sm text-slate-500 mt-1">
            Vacaciones (días hábiles) por mes y saldo de la bolsa anual por persona.
          </p>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Año</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm block"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
          >
            {aniosOpciones().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {vacQ.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          Error: {(vacQ.error as Error).message}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="text-sm border-collapse w-full">
          <thead>
            <tr className="text-slate-500">
              <th
                className="sticky left-0 z-10 bg-white text-left px-3 py-2 font-medium min-w-[12rem] border-b select-none cursor-pointer hover:bg-slate-50"
                onClick={() => handleSort("nombre")}
              >
                <span className="inline-flex items-center gap-1">
                  Empleado <SortIcon active={sortKey === "nombre"} dir={sortDir} />
                </span>
              </th>
              {MESES.map((m, i) => (
                <th
                  key={m}
                  className={`px-2 py-2 text-center font-medium border-b w-12 ${
                    i === hoyMes ? "col-hoy" : ""
                  }`}
                  title={i === hoyMes ? hoyTitulo : undefined}
                >
                  {m}
                </th>
              ))}
              {(
                [
                  ["usados", "Usados", "border-l"],
                  ["bolsa", "Bolsa", ""],
                  ["restan", "Restan", ""],
                ] as const
              ).map(([k, label, extra]) => (
                <th
                  key={k}
                  className={`px-3 py-2 text-right font-medium border-b select-none cursor-pointer hover:bg-slate-50 ${extra}`}
                  onClick={() => handleSort(k)}
                >
                  <span className="inline-flex items-center gap-1">
                    {label} <SortIcon active={sortKey === k} dir={sortDir} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr>
                <td colSpan={16} className="px-3 py-8 text-center text-slate-500">
                  Sin empleados todavía.
                </td>
              </tr>
            )}
            {filas.map((emp) => {
              const { meses: arr, usados, bolsa, restan } = emp;
              return (
                <tr key={emp.email} className="border-t">
                  <td className="sticky left-0 z-10 bg-white px-3 py-1.5 whitespace-nowrap border-r">
                    {emp.nombre}
                  </td>
                  {arr.map((n: number, i: number) => (
                    <td
                      key={i}
                      className={`px-2 py-1.5 text-center tabular-nums ${
                        i === hoyMes ? "col-hoy" : ""
                      }`}
                      style={
                        {
                          backgroundColor: bg(n),
                          ...(i === hoyMes ? { "--hoy-x": hoyX } : {}),
                        } as React.CSSProperties
                      }
                      title={i === hoyMes ? hoyTitulo : undefined}
                    >
                      {n > 0 ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/mes?anio=${anio}&mes=${i + 1}`)}
                          className="w-full hover:underline cursor-pointer"
                          title={`Ver ${MESES[i]} ${anio} en detalle`}
                        >
                          {n}
                        </button>
                      ) : (
                        ""
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium border-l">
                    {usados || ""}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{bolsa}</td>
                  <td
                    className={`px-3 py-1.5 text-right tabular-nums font-medium ${
                      restan < 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {restan}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
}
